import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, GridLegacy as Grid, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, FormControl, InputLabel, Select, MenuItem, Chip, Alert,
  Switch, FormControlLabel, Stack
} from '@mui/material';
import {
  Add as AddIcon, Person as PersonIcon, CalendarMonth as CalendarIcon
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { adminAPI } from '../../services/api';

const PanelistAvailabilityManagement = ({ 
  selectedPanelist, 
  onPanelistChange, 
  onSlotUpdate,
  panelists: propPanelists = [],
  userRole = 'admin'
}) => {
  // ===== STATE MANAGEMENT =====
  const [panelists, setPanelists] = useState(propPanelists);
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'info' });

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [bulkCreateDialogOpen, setBulkCreateDialogOpen] = useState(false);

  // Form states
  const [currentPanelistId, setCurrentPanelistId] = useState(selectedPanelist || '');
  const [slotForm, setSlotForm] = useState({
    title: 'Available for Interview',
    description: '',
    start_datetime: null,
    end_datetime: null,
    location: '',
    meeting_link: '',
    is_available: true
  });

  const [bulkForm, setBulkForm] = useState({
    panelist_id: '',
    pattern: {
      days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      start_time: '09:00',
      end_time: '17:00',
      duration: 60,
      location: '',
      meeting_link: '',
      description: 'Bulk created availability slot'
    },
    date_range: {
      start_date: new Date().toISOString().split('T')[0],
      end_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
    }
  });

  // ===== DATA LOADING =====
  const loadPanelists = useCallback(async () => {
    if (propPanelists.length > 0) {
      setPanelists(propPanelists);
      return;
    }

    try {
      console.log('📋 Loading panelists...');
      const response = await adminAPI.getPanelists();
      const panelistsData = response.data.panelists || [];
      setPanelists(panelistsData);
    } catch (error) {
      console.error('❌ Failed to load panelists:', error);
      showAlert('Failed to load panelists', 'error');
    }
  }, [propPanelists]);


  // ===== EFFECTS =====
  useEffect(() => {
    loadPanelists();
  }, [loadPanelists]);


  useEffect(() => {
    setCurrentPanelistId(selectedPanelist || '');
  }, [selectedPanelist]);

  useEffect(() => {
    if (onPanelistChange) {
      onPanelistChange(currentPanelistId);
    }
  }, [currentPanelistId, onPanelistChange]);

  // ===== UTILITY FUNCTIONS =====
  const showAlert = (message, severity = 'info') => {
    setAlert({ open: true, message, severity });
    setTimeout(() => setAlert({ open: false, message: '', severity: 'info' }), 4000);
  };

  const resetForm = () => {
    setSlotForm({
      title: 'Available for Interview',
      description: '',
      start_datetime: null,
      end_datetime: null,
      location: '',
      meeting_link: '',
      is_available: true
    });
  };


  // ===== SLOT MANAGEMENT HANDLERS =====
  const handleCreateSlot = async () => {
    try {
      if (!currentPanelistId || !slotForm.start_datetime || !slotForm.end_datetime) {
        showAlert('Please select panelist and provide start/end times', 'error');
        return;
      }

      if (slotForm.start_datetime >= slotForm.end_datetime) {
        showAlert('End time must be after start time', 'error');
        return;
      }

      console.log('📝 Admin creating slot...', slotForm);
      const slotData = {
        ...slotForm,
        start_datetime: slotForm.start_datetime.toISOString(),
        end_datetime: slotForm.end_datetime.toISOString()
      };

      await adminAPI.createPanelistSlot(currentPanelistId, slotData);
      showAlert('Availability slot created successfully!', 'success');
      setCreateDialogOpen(false);
      resetForm();

      if (onSlotUpdate) onSlotUpdate();
    } catch (error) {
      console.error('❌ Failed to create slot:', error);
      const message = error.response?.data?.error || 'Failed to create slot';
      showAlert(message, 'error');
    }
  };


  const handleBulkCreate = async () => {
    try {
      if (!bulkForm.panelist_id || bulkForm.pattern.days.length === 0) {
        showAlert('Please select panelist and at least one day', 'error');
        return;
      }

      console.log('📝 Admin bulk creating slots...', bulkForm);
      const response = await adminAPI.bulkCreateSlots(bulkForm);
      showAlert(`Successfully created ${response.data.total_created} slots!`, 'success');
      setBulkCreateDialogOpen(false);

      if (onSlotUpdate) onSlotUpdate();
    } catch (error) {
      console.error('❌ Failed to bulk create slots:', error);
      const message = error.response?.data?.error || 'Failed to bulk create slots';
      showAlert(message, 'error');
    }
  };


  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        {alert.open && (
          <Alert 
            severity={alert.severity} 
            onClose={() => setAlert({ open: false, message: '', severity: 'info' })}
            sx={{ mb: 2 }}
          >
            {alert.message}
          </Alert>
        )}

        {/* Header */}
        {/* <Box mb={3}>
          <Typography variant="h5" gutterBottom mb={2}>
            <PersonIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Panelist Availability Management
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
              disabled={!currentPanelistId}
            >
              Create Slot
            </Button>
            <Button
              variant="outlined"
              startIcon={<CalendarIcon />}
              onClick={() => {
                setBulkForm(prev => ({ ...prev, panelist_id: currentPanelistId }));
                setBulkCreateDialogOpen(true);
              }}
              disabled={!currentPanelistId}
            >
              Bulk Create
            </Button>
          </Stack>
        </Box> */}



        {/* Create Slot Dialog */}
        <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Create Availability Slot</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Title"
                  value={slotForm.title}
                  onChange={(e) => setSlotForm(prev => ({ ...prev, title: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DateTimePicker
                  label="Start Date & Time"
                  value={slotForm.start_datetime}
                  onChange={(newValue) => setSlotForm(prev => ({ ...prev, start_datetime: newValue }))}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DateTimePicker
                  label="End Date & Time"
                  value={slotForm.end_datetime}
                  onChange={(newValue) => setSlotForm(prev => ({ ...prev, end_datetime: newValue }))}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={2}
                  value={slotForm.description}
                  onChange={(e) => setSlotForm(prev => ({ ...prev, description: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Location"
                  value={slotForm.location}
                  onChange={(e) => setSlotForm(prev => ({ ...prev, location: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Meeting Link"
                  value={slotForm.meeting_link}
                  onChange={(e) => setSlotForm(prev => ({ ...prev, meeting_link: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={slotForm.is_available}
                      onChange={(e) => setSlotForm(prev => ({ ...prev, is_available: e.target.checked }))}
                    />
                  }
                  label="Available for booking"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateSlot} variant="contained">Create Slot</Button>
          </DialogActions>
        </Dialog>


        {/* Bulk Create Dialog */}
        <Dialog open={bulkCreateDialogOpen} onClose={() => setBulkCreateDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Bulk Create Weekly Slots</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Select Panelist</InputLabel>
                  <Select
                    value={bulkForm.panelist_id}
                    onChange={(e) => setBulkForm(prev => ({ ...prev, panelist_id: e.target.value }))}
                    label="Select Panelist"
                  >
                    {panelists.map(panelist => (
                      <MenuItem key={panelist.id} value={panelist.id}>
                        {panelist.username}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Start Date"
                  value={bulkForm.date_range.start_date}
                  onChange={(e) => setBulkForm(prev => ({ 
                    ...prev, 
                    date_range: { ...prev.date_range, start_date: e.target.value } 
                  }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="End Date"
                  value={bulkForm.date_range.end_date}
                  onChange={(e) => setBulkForm(prev => ({ 
                    ...prev, 
                    date_range: { ...prev.date_range, end_date: e.target.value } 
                  }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Days of Week</InputLabel>
                  <Select
                    multiple
                    value={bulkForm.pattern.days}
                    onChange={(e) => setBulkForm(prev => ({ 
                      ...prev, 
                      pattern: { ...prev.pattern, days: e.target.value } 
                    }))}
                    label="Days of Week"
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} size="small" />
                        ))}
                      </Box>
                    )}
                  >
                    {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => (
                      <MenuItem key={day} value={day}>
                        {day}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="time"
                  label="Start Time"
                  value={bulkForm.pattern.start_time}
                  onChange={(e) => setBulkForm(prev => ({ 
                    ...prev, 
                    pattern: { ...prev.pattern, start_time: e.target.value } 
                  }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="time"
                  label="End Time"
                  value={bulkForm.pattern.end_time}
                  onChange={(e) => setBulkForm(prev => ({ 
                    ...prev, 
                    pattern: { ...prev.pattern, end_time: e.target.value } 
                  }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Slot Duration (minutes)"
                  value={bulkForm.pattern.duration}
                  onChange={(e) => setBulkForm(prev => ({ 
                    ...prev, 
                    pattern: { ...prev.pattern, duration: parseInt(e.target.value) || 60 } 
                  }))}
                  inputProps={{ min: 15, max: 480 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Location"
                  value={bulkForm.pattern.location}
                  onChange={(e) => setBulkForm(prev => ({ 
                    ...prev, 
                    pattern: { ...prev.pattern, location: e.target.value } 
                  }))}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Meeting Link"
                  value={bulkForm.pattern.meeting_link}
                  onChange={(e) => setBulkForm(prev => ({ 
                    ...prev, 
                    pattern: { ...prev.pattern, meeting_link: e.target.value } 
                  }))}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBulkCreateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleBulkCreate} variant="contained">Create Bulk Slots</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default PanelistAvailabilityManagement;
